import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* Decorative illustrated left panel */
function IllustratedPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-indigo-700 via-indigo-800 to-purple-900 p-12 relative overflow-hidden">
      {/* Background geometric pattern */}
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'80\' height=\'80\' viewBox=\'0 0 80 80\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
      />

      {/* Floating orbs */}
      <div className="absolute top-16 right-10 w-40 h-40 rounded-full bg-white/5 blur-2xl" />
      <div className="absolute bottom-20 left-8  w-56 h-56 rounded-full bg-purple-400/10 blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-indigo-400/5 blur-3xl" />

      {/* Brand */}
      <div className="relative z-10">
        <div className="text-white font-bold text-3xl tracking-tight mb-1">TERAINFO</div>
        <p className="text-indigo-200 text-sm">Your intelligent daily digest</p>
      </div>

      {/* Central SVG illustration */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-12">
        <svg viewBox="0 0 320 280" fill="none" className="w-full max-w-sm" aria-hidden="true">
          {/* Stars */}
          {[[20,20],[280,30],[300,200],[15,230],[160,10]].map(([x,y],i)=>(
            <circle key={i} cx={x} cy={y} r={i%2===0?2.5:1.5} fill="white" fillOpacity="0.5"/>
          ))}
          {/* Large planet */}
          <circle cx="160" cy="140" r="72" fill="white" fillOpacity="0.06" stroke="white" strokeOpacity="0.7" strokeWidth="2"/>
          {/* Planet bands */}
          <path d="M92 125 Q160 116 228 125" stroke="white" strokeOpacity="0.25" strokeWidth="1.5" fill="none"/>
          <path d="M89 140 Q160 133 231 140" stroke="white" strokeOpacity="0.2"  strokeWidth="1.5" fill="none"/>
          <path d="M92 155 Q160 164 228 155" stroke="white" strokeOpacity="0.25" strokeWidth="1.5" fill="none"/>
          {/* Ring */}
          <ellipse cx="160" cy="140" rx="110" ry="28" stroke="white" strokeOpacity="0.6" strokeWidth="2" fill="none"/>
          {/* Satellite dots */}
          <circle cx="50" cy="80" r="18" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" fill="white" fillOpacity="0.05"/>
          <circle cx="260" cy="210" r="12" stroke="white" strokeOpacity="0.45" strokeWidth="1.5" fill="white" fillOpacity="0.04"/>
          {/* Sparkle stars */}
          <path d="M40 185 L42 180 L44 185 L49 187 L44 189 L42 194 L40 189 L35 187Z" fill="white" fillOpacity="0.6"/>
          <path d="M270 65 L272 60 L274 65 L279 67 L274 69 L272 74 L270 69 L265 67Z" fill="white" fillOpacity="0.5"/>
          <path d="M80 260 L81 257 L82 260 L85 261 L82 262 L81 265 L80 262 L77 261Z" fill="white" fillOpacity="0.45"/>
          {/* News card floating elements */}
          <rect x="95" y="100" width="130" height="18" rx="4" fill="white" fillOpacity="0.12"/>
          <rect x="95" y="124" width="100" height="12" rx="3" fill="white" fillOpacity="0.08"/>
          <rect x="95" y="142" width="115" height="12" rx="3" fill="white" fillOpacity="0.08"/>
        </svg>
      </div>

      {/* Feature list */}
      <div className="relative z-10 space-y-3">
        {[
          { icon: '💻', text: '8 live content categories' },
          { icon: '⚡', text: 'Auto-updated every 6 hours' },
          { icon: '🔒', text: 'Secure JWT authentication' },
        ].map(({ icon, text }) => (
          <div key={text} className="flex items-center gap-3">
            <span className="text-lg leading-none">{icon}</span>
            <span className="text-indigo-100 text-sm">{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login }             = useAuth();
  const navigate              = useNavigate();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message ?? 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <IllustratedPanel />

      {/* Right: form */}
      <div className="flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-sm">
          {/* Mobile brand (hidden on lg) */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-600 rounded-2xl mb-3 shadow-lg">
              <span className="text-2xl">📰</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">TERAINFO</h1>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h2>
          <p className="text-gray-500 text-sm mb-7">Sign in to your account to continue</p>

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <input
                id="email" type="email" name="email"
                value={form.email} onChange={handleChange}
                required autoComplete="email" placeholder="you@example.com"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <input
                id="password" type="password" name="password"
                value={form.password} onChange={handleChange}
                required autoComplete="current-password" placeholder="••••••••"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-7">
            New here?{' '}
            <Link to="/register" className="text-indigo-600 hover:text-indigo-800 font-semibold">
              Create a free account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
